import { z } from 'zod';

/**
 * Zod contract for the Calendly booking payload forwarded by Pabbly.
 *
 * Pabbly bundles several Calendly API step-outputs into one envelope:
 *   res1 = the invitee.* webhook trigger  -> { event, payload: { invitee + scheduled_event } }
 *   res3 = the scheduled_event resource   (redundant enrichment; fallback source only)
 *   res5 = the invitee resource           (redundant enrichment; fallback source only)
 *   res2 / res4 = { status: 'success' } step markers
 *
 * `res1.payload` is the canonical, most-complete source (invitee fields + a nested
 * scheduled_event). The processor validates the whole envelope with
 * `calendlyPabblyEnvelopeSchema`, then reads from res1.payload (falling back to
 * res3.resource / res5.resource only if a field is absent).
 *
 * Parsing is intentionally best-effort (mirrors the feedback-webhook philosophy):
 * required fields are limited to what we must have to act — event, email, timezone,
 * created_at, invitee uri, and scheduled_event.start_time. Everything else is
 * optional/passthrough so a usable-but-extra-laden payload is still accepted. The
 * full raw blob is ALWAYS preserved verbatim in calendly_bookings.raw_payload.
 */

const calendlyTrackingSchema = z
  .object({
    utm_campaign: z.string().nullish(),
    utm_source: z.string().nullish(),
    utm_medium: z.string().nullish(),
    utm_content: z.string().nullish(),
    utm_term: z.string().nullish(),
    salesforce_uuid: z.string().nullish(),
  })
  .passthrough()
  .nullish();

export const calendlyQAItemSchema = z.object({
  question: z.string(),
  // Multi-select / checkbox answers can arrive as arrays; coerce to a string at read time.
  answer: z.union([z.string(), z.array(z.string())]),
  position: z.number().nullish(),
});

const calendlyScheduledEventSchema = z
  .object({
    start_time: z.string(), // TRUE UTC ISO -> interested_leads.meeting_date
    end_time: z.string().nullish(),
    name: z.string().nullish(), // event-type name (e.g. "Intro Call (C)")
    event_type: z.string().nullish(), // event-type URI
    uri: z.string().nullish(), // scheduled_event URI; event UUID in last path segment
    status: z.string().nullish(),
    location: z
      .object({ join_url: z.string().nullish() })
      .passthrough()
      .nullish(),
    event_memberships: z
      .array(
        z
          .object({
            user_email: z.string().nullish(),
            user_name: z.string().nullish(),
          })
          .passthrough()
      )
      .nullish(),
  })
  .passthrough();

const calendlyInviteePayloadSchema = z
  .object({
    // invitee identity
    email: z.string().max(320),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    name: z.string().nullish(),
    timezone: z.string().max(100), // prospect IANA zone (e.g. "Asia/Calcutta")
    status: z.string().nullish(), // 'active' | 'canceled'
    rescheduled: z.boolean().nullish(),
    no_show: z.boolean().nullish(),
    old_invitee: z.string().nullish(), // reschedule predecessor URI
    new_invitee: z.string().nullish(),
    cancel_url: z.string().nullish(),
    reschedule_url: z.string().nullish(),
    created_at: z.string(), // ISO; booking instant + newest-event-wins clock
    updated_at: z.string().nullish(),
    uri: z.string(), // invitee URI; invitee UUID in last path segment
    tracking: calendlyTrackingSchema,
    questions_and_answers: z.array(calendlyQAItemSchema).nullish(),
    // nested scheduled_event (canonical meeting details)
    scheduled_event: calendlyScheduledEventSchema,
  })
  .passthrough();

/** res1 — the invitee.* webhook trigger. */
export const calendlyInviteeTriggerSchema = z
  .object({
    event: z.string(), // 'invitee.created' | 'invitee.canceled'
    created_at: z.string().nullish(), // when Calendly emitted this trigger
    payload: calendlyInviteePayloadSchema,
  })
  .passthrough();

/** Full Pabbly envelope. res1 is required + validated; res3/res5 are passthrough fallbacks. */
export const calendlyPabblyEnvelopeSchema = z
  .object({
    res1: calendlyInviteeTriggerSchema,
    res2: z.unknown().nullish(),
    // Pabbly step outputs; their shape varies (and may be {status:'failed'} mid-pipeline). The
    // processor only ever reads res1, so never reject the whole envelope over res3/res5.
    res3: z.unknown().nullish(),
    res4: z.unknown().nullish(),
    res5: z.unknown().nullish(),
  })
  .passthrough();

export type CalendlyPabblyEnvelope = z.infer<typeof calendlyPabblyEnvelopeSchema>;
export type CalendlyInviteeTrigger = z.infer<typeof calendlyInviteeTriggerSchema>;
export type CalendlyQAItem = z.infer<typeof calendlyQAItemSchema>;

// ---------------------------------------------------------------------------
// Flat payload — the RECOMMENDED Pabbly "API by Pabbly" contract.
//
// Pabbly's API action maps discrete trigger variables (its Set Parameters rows),
// not whole nested objects — the envelope above proved un-sendable from Pabbly in
// practice (the Aug 2026 outage: {"data": ""}). The flat contract instead takes
// our own top-level keys, one per mapped variable, e.g.:
//   { "event": "invitee.created", "email": "x@y.com", "start_time": "2026-08-31T05:00:00.000000Z", ... }
//
// Required: email + start_time ONLY — every client's Calendly can provide those
// (plus name, which is Calendly-mandatory but tolerated missing here). Everything
// else is optional: an unmapped Pabbly variable arrives as "" and coerces to null,
// so a client without a phone/website question can never be blocked. Unknown keys
// pass through and are preserved verbatim in calendly_bookings.raw_payload.
// ---------------------------------------------------------------------------

/** Pabbly sends every mapped variable as a string; an unmapped one arrives as "". */
const flatTrim = (v: unknown): unknown => {
  if (typeof v === 'string') {
    const s = v.trim();
    return s === '' ? null : s;
  }
  return v;
};

const flatString = z.preprocess(flatTrim, z.string().nullish());

/** Required variant: "" / whitespace → undefined so Zod reports it as missing. */
const flatStringRequired = z.preprocess((v) => {
  if (typeof v === 'string' && v.trim() === '') return undefined;
  if (typeof v === 'string') return v.trim();
  return v;
}, z.string());

/** Pabbly booleans arrive as true/false, 0/1, or "0"/"1"/"true"/"false"; unknown → null. */
const flatBoolean = z.preprocess((v): unknown => {
  if (v == null) return null;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === '') return null;
    if (s === 'true' || s === '1' || s === 'yes' || s === 'y') return true;
    if (s === 'false' || s === '0' || s === 'no' || s === 'n') return false;
  }
  return null;
}, z.boolean().nullish());

/**
 * Q&A may arrive as an array or as a JSON-encoded string (Pabbly stringifies mapped
 * objects). Any parse/validation failure degrades to null — a decorative field must
 * never block ingestion.
 */
const flatQA = z.preprocess((v): unknown => {
  if (v == null) return null;
  if (typeof v === 'string') {
    const s = v.trim();
    if (!s) return null;
    try {
      return JSON.parse(s);
    } catch {
      return null;
    }
  }
  return v;
}, z.array(calendlyQAItemSchema).nullish().catch(null));

export const calendlyFlatPayloadSchema = z
  .object({
    // lifecycle (defaults handled by the processor: event → 'invitee.created')
    event: flatString,
    email: flatStringRequired,
    start_time: flatStringRequired,
    // identity
    first_name: flatString,
    last_name: flatString,
    name: flatString,
    phone: flatString,
    website: flatString,
    // meeting
    timezone: flatString,
    end_time: flatString,
    event_name: flatString,
    event_type_uri: flatString,
    event_uri: flatString,
    meeting_link: flatString,
    join_url: flatString, // alias for meeting_link
    host_email: flatString,
    // booking instant (falls back to now)
    created_at: flatString,
    updated_at: flatString,
    // idempotency: invitee URI or bare UUID (a synthetic key is derived if absent)
    invitee_uri: flatString,
    // lifecycle detail
    status: flatString,
    rescheduled: flatBoolean,
    no_show: flatBoolean,
    old_invitee: flatString,
    new_invitee: flatString,
    cancel_url: flatString,
    reschedule_url: flatString,
    // tracking
    utm_campaign: flatString,
    utm_source: flatString,
    utm_medium: flatString,
    utm_content: flatString,
    utm_term: flatString,
    salesforce_uuid: flatString,
    // optional structured extras
    questions_and_answers: flatQA,
  })
  .passthrough();

export type CalendlyFlatPayload = z.infer<typeof calendlyFlatPayloadSchema>;

// ---------------------------------------------------------------------------
// Mapping-completeness linting (Pabbly Set Parameters setup aid).
//
// These keys are emitted by EVERY Calendly invitee.created payload (their values
// may legitimately be empty — e.g. old_invitee is null on a first booking). A
// Pabbly "Set Parameters" row that isn't mapped simply never sends the key, so
// KEY ABSENCE in a stored flat payload is a reliable signal that the operator
// skipped that row — surfaced as an "unmapped" badge in the Events drawer so
// they can complete the mapping. Envelope-format payloads (res1) are skipped:
// they're forwarded wholesale, not row-mapped.
// ---------------------------------------------------------------------------

export const CALENDLY_ALWAYS_PROVIDED_KEYS = [
  'name',
  'first_name',
  'last_name',
  'timezone',
  'created_at',
  'rescheduled',
  'old_invitee',
  'event_name',
  // The invitee's phone-question answer. Treated as always-provided per operator
  // decision (2026-08-26): a dropped phone row silently loses numbers + Tier-2
  // matching, so the Events-drawer lint must catch it. Caveat: a client whose
  // Calendly form has NO phone question will show a persistent badge — ignore it
  // there (the key can never arrive).
  'phone',
] as const;

/** Keys from CALENDLY_ALWAYS_PROVIDED_KEYS that are entirely absent from a flat payload. */
export function findMissingMappedCalendlyFields(
  payload: unknown,
  keys: readonly string[] = CALENDLY_ALWAYS_PROVIDED_KEYS
): string[] {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return [];
  const obj = payload as Record<string, unknown>;
  if ('res1' in obj) return []; // envelope format — not row-mapped, nothing to lint
  const present = new Set(Object.keys(obj));
  return keys.filter((k) => !present.has(k));
}

// ---------------------------------------------------------------------------
// Cal.com (cal.com booking webhook via Pabbly) — same flat-key contract, with a
// cal.com-specific always-provided list. Pabbly maps the cal.com TriggerEvent
// variable verbatim into `event` (BOOKING_CREATED / BOOKING_RESCHEDULED /
// BOOKING_CANCELLED); rescheduled/old_invitee have NO cal.com equivalent (the
// lifecycle is derived from the TriggerEvent alone), so they are absent from
// this list — their key absence is EXPECTED, not an unmapped-row problem.
// ---------------------------------------------------------------------------

export const CALCOM_ALWAYS_PROVIDED_KEYS = [
  'name',
  'first_name',
  'last_name',
  'timezone',
  'created_at',
  'event_name',
  // Same operator decision as the Calendly phone key: a dropped phone row
  // silently loses numbers + Tier-2 matching, so lint it.
  'phone',
] as const;
